import React from "react"
import { Box } from "@mui/material"
import { ThemeProvider } from "@mui/material/styles"
import {
  createViraTheme,
  type ViraTheme,
  type ViraThemeMode,
} from "./createViraTheme"

export type ViraThemeProviderProps = {
  /** Opt-in color mode. Defaults to `light`. Ignored when `theme` is passed. */
  mode?: ViraThemeMode
  /** Full theme override. When set, `mode` is ignored. */
  theme?: ViraTheme
  children: React.ReactNode
}

/**
 * Provides MUI theme + CSS variables (`--vira-*`) so legacy makeStyles /
 * Victory charts can follow light/dark without rewriting every consumer.
 */
export function ViraThemeProvider({
  mode = "light",
  theme,
  children,
}: ViraThemeProviderProps) {
  const resolved = React.useMemo(
    () => theme ?? createViraTheme({ mode }),
    [theme, mode],
  )
  const vira = resolved.palette.vira
  const paper = resolved.palette.background.paper

  return (
    <ThemeProvider theme={resolved}>
      <Box
        data-vira-theme={resolved.palette.mode}
        sx={{
          // Expose tokens for legacy CSS / Victory / MUI v4 makeStyles
          "--vira-ink": vira.ink,
          "--vira-muted": vira.muted,
          "--vira-subtle": vira.subtle,
          "--vira-border": vira.border,
          "--vira-paper": paper,
          "--vira-page-bg": vira.pageBg,
          "--vira-soft-surface": vira.softSurface,
          "--vira-disabled": vira.disabledText,
          "--vira-lavender-text": vira.lavenderText,
          "--vira-nav-active": vira.navActiveBg,
          color: vira.ink,
          bgcolor: vira.pageBg,
          flex: "1 1 0",
          width: "100%",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  )
}
